import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { SelectBox } from 'cbc-shared-lib';
import { ControlContainer, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { CodeConversionMstDto } from 'sg-api';
import { EnergyTypeCode } from '../../../../../sg-kc-shared/constants/energyTypeCode ';
import { ReferenceTableCode } from '../../../../../sg-kc-shared/constants/referenceTableCode';

/**
 * 早見表出力画面(出力条件)
 * @exports
 * @class Sgkc041dReferenceTableOutputConditionComponent
 * @implements {OnInit, OnDestroy, OnChanges}
 */
@Component({
    selector: 'app-sgkc041d-reference-table-output-condition',
    templateUrl: './sgkc041d-reference-table-output-condition.component.html',
    styleUrl: './sgkc041d-reference-table-output-condition.component.scss',
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})

export class Sgkc041dReferenceTableOutputConditionComponent implements OnInit, OnDestroy, OnChanges {
    /** 出力ボタン押下イベント */
    @Output() onClickOutputEvent = new EventEmitter<void>();
    /** コード変換マスタレスポンス */
    @Input() codeConversionMstMap: Map<string, Array<CodeConversionMstDto>> = null;
    /** 検索条件に入力した対象年月 */
    @Input() searchTargetYm: string = null;
    /** 検索条件に入力したエネルギー種別 */
    @Input() energyClscode: string = null;
    /** 出力上限 */
    empamtUpperlimit: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false }, Validators.required);
    /** 出力間隔 */
    referencetableOutputintervalCode: UntypedFormControl = new UntypedFormControl({ value: '', disabled: true }, Validators.required);
    /** 対象年月 */
    chargeCalcYm: UntypedFormControl = new UntypedFormControl({ value: '', disabled: true }, Validators.required);
    /** 親コンポーネント保持領域 */
    parentForm: UntypedFormGroup;;
    /** 検索情報格納FormGroup */
    referenceTableForm: UntypedFormGroup = this.fb.group({
        chargeCalcYm: this.chargeCalcYm,
        empamtUpperlimit: this.empamtUpperlimit,
        referencetableOutputintervalCode: this.referencetableOutputintervalCode,
    });

    /** 並び替え前の早見表出力間隔リスト */
    preReferenceTableList: SelectBox[] = [];
    /** 画面表示用早見表出力間隔リスト */
    referenceTableList: SelectBox[];

    /**
     * コンストラクタ
     * @param {UntypedFormBuilder} fb フォームビルダー
     * @param {FormGroupDirective} parent 親フォームグループディレクティブ
     * @memberof Sgkc041dReferenceTableOutputConditionComponent
     */
    constructor(
        private fb: UntypedFormBuilder,
        private parent: FormGroupDirective,) {
    }

    /**
     * 初期処理
     * @memberof Sgkc041dReferenceTableOutputConditionComponent
     */
    ngOnInit(): void {
        this.parentForm = this.parent.form;
        this.parentForm.addControl('referenceTableForm', this.referenceTableForm);
    }

    /**
     * コンポーネント破棄処理
     * @memberof Sgkc041dReferenceTableOutputConditionComponent
     */
    ngOnDestroy(): void {
        if (this.parentForm) {
            this.parentForm.clearValidators();
            this.parentForm.updateValueAndValidity();
            this.parentForm.removeControl('referenceTableForm');
        }
    }

    /**
    * プロパティが変更されたときに、その変更に関する情報を提供する。
    * @param {SimpleChanges} changes
    */
    ngOnChanges(changes: SimpleChanges) {
        // 対象プロパティが変更された場合のみ処理する
        if (changes.codeConversionMstMap && this.codeConversionMstMap) {
            this.preReferenceTableList.push(...this.getSelectBoxFromCodeConversionMst("S100180"));
            // エネルギー種別に応じて早見表出力間隔を並び替え、活性を制御
            if (this.energyClscode === EnergyTypeCode.toshiGas) {
                // 都市ガスのとき初期表示に1㎥を設定
                // 1㎥を先頭に並び替え
                const targetValue = ReferenceTableCode.one;
                const targetItems = this.preReferenceTableList.filter(item => item.value === targetValue);
                const otherItems = this.preReferenceTableList.filter(item => item.value !== targetValue);
                this.preReferenceTableList = [...targetItems, ...otherItems];
                this.referenceTableList = [
                    ...this.preReferenceTableList.map(data => ({
                        value: data.value,
                        viewValue: data.viewValue,
                    }))]
            } else if (this.energyClscode === EnergyTypeCode.lpGas) {
                // LPガスのとき初期表示に0.5㎥を設定
                // 0.5㎥を先頭に並び替え
                const targetValue = ReferenceTableCode.zerofive;
                const targetItems = this.preReferenceTableList.filter(item => item.value === targetValue);
                const otherItems = this.preReferenceTableList.filter(item => item.value !== targetValue);
                this.preReferenceTableList = [...targetItems, ...otherItems];
                this.referenceTableList = [
                    ...this.preReferenceTableList.map(data => ({
                        value: data.value,
                        viewValue: data.viewValue,
                    }))]
                // フォームを活性化
                this.referenceTableForm.controls['referencetableOutputintervalCode'].enable();
            }
            // フォームに初期値をセット
            this.referenceTableForm.get('chargeCalcYm').setValue(this.searchTargetYm.replace("-", ""));
            this.referenceTableForm.get('referencetableOutputintervalCode').setValue(this.referenceTableList[0].value);
        }
    }

    /**
     * 出力ボタン押下時処理
     * @memberof Sgkc041dReferenceTableOutputConditionComponent
     */
    onClickOutput(): void {
        this.onClickOutputEvent.emit();
    }

    /**
     * コード変換マスタからSelectBoxリストを取得する
     * @param {string} codeId コードID
     * @returns {SelectBox[]} SelectBoxリスト
     * @memberof Sgkc041dReferenceTableOutputConditionComponent
     */
    getSelectBoxFromCodeConversionMst(codeId: string): SelectBox[] {
        let retSelectBoxList: SelectBox[] = [];
        let codeList: CodeConversionMstDto[] = [];
        if (this.codeConversionMstMap.has(codeId)) {
            codeList = this.codeConversionMstMap.get(codeId);
            if (codeList) {
                codeList.forEach(data => {
                    const select: SelectBox = { value: data.userCode, viewValue: data.hyojiCont };
                    retSelectBoxList.push(select);
                })
            }
        }
        return retSelectBoxList;
    }

}
